import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import {
  TimeAttendanceBreakExportFromAPI,
  TimeAttendanceDailyExportFromAPI,
} from 'types/timeAttendance/timeAttendanceTypes';

const BORDER_STYLE: Partial<ExcelJS.Borders> = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
};

const HEADER_FILL = {
  type: 'pattern' as const,
  pattern: 'solid' as const,
  fgColor: { argb: 'FFD9D9D9' },
};

const isZeroDate = (value?: string | null) =>
  !value || value === '0000-00-00 00:00:00' || value === '0000-00-00';

const parseDate = (value?: string | null) => {
  if (isZeroDate(value)) return null;

  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value?: string | null) => {
  const date = parseDate(value);
  if (!date) return '-';

  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getFullYear()),
  ].join('/');
};

const formatDateTime = (value?: string | null) => {
  const date = parseDate(value);
  if (!date) return '-';

  return `${formatDate(value)} ${String(date.getHours()).padStart(
    2,
    '0'
  )}:${String(date.getMinutes()).padStart(2, '0')}:${String(
    date.getSeconds()
  ).padStart(2, '0')}`;
};

const formatHourValue = (value?: number | string | null) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return 0;

  return Number(numeric.toFixed(2));
};

const convertHoursToExcelTime = (value?: number | string | null) => {
  const hours = formatHourValue(value);
  return hours / 24;
};

const getNetWorkingHoursCellValue = (value: number) => {
  if (value < 0) return '-';
  return convertHoursToExcelTime(value);
};

const formatBreakDetails = (breaks: TimeAttendanceBreakExportFromAPI[]) => {
  if (!breaks.length) return '-';

  return breaks
    .map((item) => {
      const startDate = parseDate(item.break_start_at);
      const endDate = parseDate(item.break_end_at);
      const start = startDate
        ? `${String(startDate.getHours()).padStart(2, '0')}:${String(
            startDate.getMinutes()
          ).padStart(2, '0')}`
        : '-';
      const end = endDate
        ? `${String(endDate.getHours()).padStart(2, '0')}:${String(
            endDate.getMinutes()
          ).padStart(2, '0')}`
        : '-';
      const note = item.note?.trim() ? ` [${item.note.trim()}]` : '';

      return `${start}-${end} (${formatHourValue(item.breaking_hours)} ชม.)${note}`;
    })
    .join(',\n');
};

const setCommonCellStyle = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.border = BORDER_STYLE;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };
  });
};

export const exportTimeAttendanceDailyExcel = async (
  _t: TFunction,
  data: TimeAttendanceDailyExportFromAPI[],
  getStoreNameById: (id: number) => string,
  startDate: string,
  endDate: string
) => {
  const formattedDateRange = `${startDate}_to_${endDate}`;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Daily Report');

  worksheet.columns = [
    { key: 'no', width: 8 },
    { key: 'work_date', width: 16 },
    { key: 'office_code', width: 16 },
    { key: 'full_name', width: 30 },
    { key: 'branch_name', width: 18 },
    { key: 'clock_in_at', width: 22 },
    { key: 'clock_out_at', width: 22 },
    { key: 'working_hours', width: 20 },
    { key: 'breaking_hours', width: 20 },
    { key: 'net_working_hours', width: 14 },
    { key: 'break_details', width: 44 },
    { key: 'note', width: 24 },
  ];

  const dateText = `${formatDate(startDate)} - ${formatDate(endDate)}`;

  worksheet.mergeCells('A1:L1');
  const titleCell = worksheet.getCell('A1');
  titleCell.value = `รายวัน (Daily Report) ${dateText}`;
  titleCell.font = { bold: true, size: 12 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.border = BORDER_STYLE;
  titleCell.fill = HEADER_FILL;

  const headerRow = worksheet.getRow(2);
  headerRow.values = [
    'NO',
    'วันที่ทำงาน',
    'รหัสพนักงาน',
    'ชื่อ-นามสกุล',
    'สาขา',
    'เวลาเข้างาน',
    'เวลาออกงาน',
    'เวลาทำงาน (ชม:นาที)',
    'เวลาพัก (ชม:นาที)',
    'ชั่วโมงสุทธิ',
    'รายละเอียดพัก',
    'หมายเหตุ',
  ];
  headerRow.font = { bold: true, size: 12 };
  headerRow.height = 24;
  setCommonCellStyle(headerRow);
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL;
  });

  data.forEach((item, index) => {
    const workingHours = formatHourValue(item.working_hours);
    const breakingHours = formatHourValue(item.breaking_hours);
    const netWorkingHours = Number((workingHours - breakingHours).toFixed(2));

    const row = worksheet.addRow({
      no: index + 1,
      work_date: formatDate(item.work_date),
      office_code: item.office_code || '-',
      full_name: item.full_name || '-',
      branch_name: getStoreNameById(item.store_id),
      clock_in_at: formatDateTime(item.clock_in_at),
      clock_out_at: formatDateTime(item.clock_out_at),
      working_hours: convertHoursToExcelTime(workingHours),
      breaking_hours: convertHoursToExcelTime(breakingHours),
      net_working_hours: getNetWorkingHoursCellValue(netWorkingHours),
      break_details: formatBreakDetails(item.attendance_breaks || []),
      note: item.note || '-',
    });

    row.height = Math.max(22, 18 * (item.attendance_breaks?.length || 1));
    row.font = { size: 10 };
    setCommonCellStyle(row);

    ['H', 'I'].forEach((columnKey) => {
      row.getCell(columnKey).numFmt = '[h]:mm';
    });
    if (netWorkingHours >= 0) {
      row.getCell('J').numFmt = '[h]:mm';
    }

    row.getCell('D').alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };
    row.getCell('E').alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };
    row.getCell('K').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true,
    };
    row.getCell('L').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true,
    };

    row.getCell('C').numFmt = '@';
  });

  worksheet.views = [{ state: 'frozen', ySplit: 2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer]),
    `time_attendance_daily_report_${formattedDateRange}.xlsx`
  );
};
